import { motion } from "framer-motion";
import { MapPin, Phone, Navigation, Clock } from "lucide-react";
import { useT } from "../i18n";
import { MAP_EMBED, DIRECTIONS_URL } from "../data/content";

export const Contact = () => {
  const t = useT();
  const days = [
    [t.day_mon, t.hours_open], [t.day_tue, t.hours_open], [t.day_wed, t.hours_open],
    [t.day_thu, t.hours_open], [t.day_fri, t.hours_open], [t.day_sat, t.hours_open],
    [t.day_sun, t.hours_closed],
  ];

  return (
    <section id="visit" className="bg-bergamote-50 py-24 sm:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-bergamote-600 mb-5"
        >
          {t.ch5_label}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl text-bergamote-900"
          data-testid="contact-title"
        >
          {t.ch5_title}
        </motion.h2>

        <div className="mt-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="rounded-[2rem] overflow-hidden border border-bergamote-600/20 shadow-lg shadow-bergamote-600/10">
              <iframe
                title="La Bergamote Genève — map"
                data-testid="map-iframe"
                src={MAP_EMBED}
                className="w-full h-[320px] grayscale-[35%] contrast-[1.05]"
                loading="lazy"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-4" data-testid="contact-details">
                <a
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-address-link"
                  className="flex items-start gap-3 group"
                >
                  <MapPin className="w-5 h-5 text-saffron-600 mt-0.5 shrink-0" />
                  <span className="text-bergamote-900 group-hover:text-saffron-600 transition-colors duration-300 font-medium">{t.contact_address}</span>
                </a>
                <a href="tel:+41223122333" data-testid="contact-phone-link" className="flex items-start gap-3 group">
                  <Phone className="w-5 h-5 text-saffron-600 mt-0.5 shrink-0" />
                  <span className="text-bergamote-900 group-hover:text-saffron-600 transition-colors duration-300 font-medium">{t.contact_phone}</span>
                </a>
                <a
                  href={DIRECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="directions-button"
                  className="inline-flex items-center gap-2 rounded-full border border-bergamote-600/40 text-bergamote-800 px-5 py-2.5 text-sm font-semibold hover:bg-bergamote-100 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
                >
                  <Navigation className="w-4 h-4" /> {t.contact_directions}
                </a>
              </div>

              <div className="bg-cream-50 border border-bergamote-600/15 rounded-2xl p-6" data-testid="hours-table">
                <h3 className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-bergamote-600 mb-4">
                  <Clock className="w-4 h-4" /> {t.contact_hours_title}
                </h3>
                <ul className="space-y-2 text-sm">
                  {days.map(([d, h]) => (
                    <li key={d} className="flex justify-between">
                      <span className="text-bergamote-800">{d}</span>
                      <span className={h === t.hours_closed ? "text-destructive font-medium" : "text-bergamote-900 font-medium"}>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="mt-14 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href="/_files/ugd/bbb9df_ac87e01cbc2f416ab03b45184d98ab77.pdf?v=13"
            data-testid="contact-view-menu-btn"
            className="inline-flex items-center rounded-full border border-bergamote-600/40 text-bergamote-800 px-9 py-4 text-sm font-semibold hover:bg-bergamote-100 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            {t.hero_cta_menu}
          </a>
          <a
            href="https://www.thefork.com/restaurant/la-bergamote-r752240"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-book-table-btn"
            className="inline-flex items-center rounded-full bg-bergamote-600 text-cream-50 px-9 py-4 text-sm font-semibold hover:bg-bergamote-700 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            {t.nav_reserve}
          </a>
        </motion.div>
      </div>
    </section>
  );
};
